import React, { useState, useEffect, useCallback } from 'react';
import './MyStudents.css';
import collegeConfig from '../../config/collegeConfig';
import { generateStudentDirectoryPDF } from '../../utils/studentDirectoryPDFGenerator';
import {
  FaUsers,
  FaSearch,
  FaTimes,
  FaSave,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaUserLock,
  FaToggleOn,
  FaToggleOff,
  FaFilePdf,
} from 'react-icons/fa';

// ─────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────
const studentName = (s) =>
  `${s.profile?.firstName || ''} ${s.profile?.lastName || ''}`.trim() || s.email || '?';

const studentInitials = (s) => {
  const f = s.profile?.firstName?.[0] || '';
  const l = s.profile?.lastName?.[0] || '';
  return (f + l).toUpperCase() || '?';
};

// Shows the raw batch (e.g. "2025-2028") plus the admin-defined label as a tag, when set
const formatBatch = (batch, batchLabels) =>
  batchLabels[batch] ? `${batch} (${batchLabels[batch]})` : batch;

// ─────────────────────────────────────────────────────────
// Sub-component: Student grid for selecting students
// ─────────────────────────────────────────────────────────
const StudentGrid = ({ allStudents, selectedIds, onToggle, onSelectAll, onDeselectAll, readOnly, batchLabels = {} }) => {
  const [search, setSearch] = useState('');

  const filtered = allStudents
    .filter((s) => {
      if (!search) return true;
      const n = studentName(s).toLowerCase();
      const e = (s.email || '').toLowerCase();
      return n.includes(search.toLowerCase()) || e.includes(search.toLowerCase());
    })
    .sort((a, b) => studentName(a).localeCompare(studentName(b)));

  return (
    <div className="ms-student-grid-wrapper">
      <div className="ms-grid-toolbar">
        <div className="ms-search-box">
          <FaSearch className="ms-search-icon" />
          <input
            type="text"
            placeholder="Search students..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ms-search-input"
            readOnly={readOnly}
          />
          {search && (
            <button className="ms-search-clear" onClick={() => setSearch('')}>
              <FaTimes />
            </button>
          )}
        </div>
        {!readOnly && (
          <div className="ms-bulk-actions">
            <button className="ms-btn ms-btn-sm ms-btn-outline" onClick={onSelectAll}>
              All
            </button>
            <button className="ms-btn ms-btn-sm ms-btn-outline" onClick={onDeselectAll}>
              None
            </button>
          </div>
        )}
        <span className="ms-count-label">
          {selectedIds.length}/{allStudents.length} selected
        </span>
      </div>

      {filtered.length === 0 ? (
        <p className="ms-no-results">No students match your search.</p>
      ) : (
        <div className="ms-student-grid">
          {filtered.map((s) => {
            const id = String(s._id || s.id);
            const selected = selectedIds.includes(id);
            return (
              <div
                key={id}
                className={`ms-student-card ${selected ? 'selected' : ''} ${readOnly ? 'readonly' : ''}`}
                onClick={() => !readOnly && onToggle(id)}
              >
                <div className="ms-student-avatar">{studentInitials(s)}</div>
                <div className="ms-student-body">
                  <span className="ms-student-name">{studentName(s)}</span>
                  {s.batch && (
                    <span className="ms-student-batch-badge">{formatBatch(s.batch, batchLabels)}</span>
                  )}
                  {s.profile?.registrationNumber && (
                    <span className="ms-student-reg">{s.profile.registrationNumber}</span>
                  )}
                </div>
                <div className="ms-student-check">
                  {selected ? (
                    <FaCheckCircle className="ms-check-icon selected" />
                  ) : (
                    <div className="ms-check-circle" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────
const MyStudents = ({
  user,
  assignedCourse,
  allStudents,          // already loaded in TeacherDashboard
  myStudentIds,         // teacher's own selection (used in Teachers Select mode)
  setMyStudentIds,
  batchLabels = {},     // admin-defined batch name labels, keyed by raw batch (e.g. "2026-2029")
}) => {
  const token = localStorage.getItem('token');
  const isHOD = user?.profile?.isHOD === true;

  // Filter students to valid batches (start year >= currentYear-4)
  const minBatch = new Date().getFullYear() - 4;
  const validStudents = allStudents.filter(s => {
    if (!s.batch) return true; // include students with no batch set
    const startYear = parseInt(s.batch.split('-')[0]);
    return isNaN(startYear) || startYear >= minBatch;
  });

  // ── shared state ──
  const [assignmentMode, setAssignmentMode] = useState('teachers_select');
  const [modeLoading, setModeLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState('');

  // ── HOD-specific state ──
  const [hodTeachers, setHodTeachers] = useState([]);   // [{ teacherId, name, isHOD, selectedStudentIds }]
  const [hodAssignments, setHodAssignments] = useState({}); // { teacherId: string[] }
  const [expandedTeacherId, setExpandedTeacherId] = useState(null);
  const [savingTeacherId, setSavingTeacherId] = useState(null);
  const [savingAll, setSavingAll] = useState(false);

  // ── teachers-select state (own selection) ──
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  // ── PDF download ──
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const handleDownloadPDF = async (studentList) => {
    setPdfGenerating(true);
    try {
      await generateStudentDirectoryPDF(studentList, {
        collegeName: collegeConfig.collegeName,
        logoSrc: collegeConfig.logo,
        mode: 'teacher',
        courseName: assignedCourse?.name || '',
      });
    } catch (err) {
      console.error('PDF generation error:', err);
    } finally {
      setPdfGenerating(false);
    }
  };

  // ─── fetch mode on mount ───────────────────────────────
  const fetchMode = useCallback(async () => {
    if (!assignedCourse) return;
    try {
      setModeLoading(true);
      const res = await fetch(
        `${collegeConfig.api.baseURL}/teachers/assignment-mode/${assignedCourse._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setAssignmentMode(data.mode || 'teachers_select');
        if (isHOD && data.teachers) {
          setHodTeachers(data.teachers);
          const map = {};
          data.teachers.forEach((t) => {
            map[t.teacherId] = t.selectedStudentIds || [];
          });
          setHodAssignments(map);
        }
      }
    } catch (err) {
      console.error('Error fetching assignment mode:', err);
    } finally {
      setModeLoading(false);
    }
  }, [assignedCourse, token, isHOD]);

  useEffect(() => {
    fetchMode();
  }, [fetchMode]);

  // ─── change mode (HOD only) ────────────────────────────
  const handleModeChange = async (newMode) => {
    if (!isHOD || newMode === assignmentMode) return;
    try {
      const res = await fetch(
        `${collegeConfig.api.baseURL}/teachers/assignment-mode/${assignedCourse._id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ mode: newMode })
        }
      );
      if (res.ok) {
        setAssignmentMode(newMode);
        showSuccess(newMode === 'hod_assigns' ? 'HOD Assigns mode enabled' : 'Teachers Select mode enabled');
        // Refresh to get latest teacher selections
        fetchMode();
      }
    } catch (err) {
      console.error('Error changing mode:', err);
    }
  };

  // ─── HOD: toggle student for a teacher ────────────────
  const hodToggle = (teacherId, studentId) => {
    setHodAssignments((prev) => {
      const cur = prev[teacherId] || [];
      return {
        ...prev,
        [teacherId]: cur.includes(studentId)
          ? cur.filter((id) => id !== studentId)
          : [...cur, studentId]
      };
    });
  };

  const hodSelectAll = (teacherId) => {
    setHodAssignments((prev) => ({
      ...prev,
      [teacherId]: validStudents.map((s) => String(s._id || s.id))
    }));
  };

  const hodDeselectAll = (teacherId) => {
    setHodAssignments((prev) => ({ ...prev, [teacherId]: [] }));
  };

  // ─── HOD: save one teacher ─────────────────────────────
  const saveOneTeacher = async (teacherId) => {
    setSavingTeacherId(teacherId);
    try {
      const res = await fetch(
        `${collegeConfig.api.baseURL}/teachers/hod-assign/${assignedCourse._id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            assignments: [{ teacherId, selectedStudentIds: hodAssignments[teacherId] || [] }]
          })
        }
      );
      if (res.ok) showSuccess('Saved');
    } catch (err) {
      console.error('Error saving teacher assignment:', err);
    } finally {
      setSavingTeacherId(null);
    }
  };

  // ─── HOD: save all teachers ────────────────────────────
  const saveAllTeachers = async () => {
    setSavingAll(true);
    try {
      const assignments = hodTeachers.map((t) => ({
        teacherId: t.teacherId,
        selectedStudentIds: hodAssignments[t.teacherId] || []
      }));
      const res = await fetch(
        `${collegeConfig.api.baseURL}/teachers/hod-assign/${assignedCourse._id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ assignments })
        }
      );
      if (res.ok) showSuccess('All assignments saved successfully');
    } catch (err) {
      console.error('Error saving all assignments:', err);
    } finally {
      setSavingAll(false);
    }
  };

  // ─── Regular teacher: own selection ───────────────────
  const toggleOwn = (id) => {
    setMyStudentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const saveOwn = async () => {
    if (!assignedCourse) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${collegeConfig.api.baseURL}/teachers/my-students/${assignedCourse._id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ selectedStudentIds: myStudentIds })
        }
      );
      if (res.ok) showSuccess('Student selection saved successfully!');
    } catch (err) {
      console.error('Error saving selection:', err);
    } finally {
      setSaving(false);
    }
  };

  const showSuccess = (msg) => {
    setSaveSuccess(msg);
    setTimeout(() => setSaveSuccess(''), 3000);
  };

  // ─── Render helpers ────────────────────────────────────
  const ModeToggle = () => (
    <div className="ms-mode-toggle">
      <span className="ms-mode-label">Assignment mode:</span>
      <div className="ms-toggle-pill">
        <button
          className={`ms-toggle-option ${assignmentMode === 'teachers_select' ? 'active' : ''}`}
          onClick={() => handleModeChange('teachers_select')}
        >
          {assignmentMode === 'teachers_select' ? <FaToggleOn className="ms-toggle-icon" /> : <FaToggleOff className="ms-toggle-icon" />}
          Teachers Select
        </button>
        <button
          className={`ms-toggle-option ${assignmentMode === 'hod_assigns' ? 'active hod' : ''}`}
          onClick={() => handleModeChange('hod_assigns')}
        >
          {assignmentMode === 'hod_assigns' ? <FaToggleOn className="ms-toggle-icon" /> : <FaToggleOff className="ms-toggle-icon" />}
          HOD Assigns
        </button>
      </div>
    </div>
  );

  // ─── Loading ───────────────────────────────────────────
  if (modeLoading) {
    return (
      <div className="ms-container">
        <div className="ms-loading">
          <div className="spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────
  // CASE 1: Regular teacher + HOD assigns → read-only
  // ─────────────────────────────────────────────────────
  if (!isHOD && assignmentMode === 'hod_assigns') {
    return (
      <div className="ms-container">
        <div className="ms-header">
          <h2><FaUsers style={{ marginRight: 10 }} />My Students</h2>
          <p className="ms-subtitle">
            {assignedCourse?.name} ({assignedCourse?.code})
          </p>
        </div>

        <div className="ms-hod-banner">
          <FaUserLock className="ms-hod-banner-icon" />
          <div>
            <strong>Student assignments managed by HOD</strong>
            <p>Your assigned students are selected by your department HOD. Contact the HOD to change your assignments.</p>
          </div>
        </div>

        <div className="ms-readonly-header">
          <span className="ms-count-label">{myStudentIds.length} students assigned to you</span>
        </div>

        {myStudentIds.length === 0 ? (
          <p className="ms-no-results">No students have been assigned to you yet.</p>
        ) : (
          <div className="ms-student-grid">
            {validStudents
              .filter((s) => myStudentIds.includes(String(s._id || s.id)))
              .sort((a, b) => studentName(a).localeCompare(studentName(b)))
              .map((s) => {
                const id = String(s._id || s.id);
                return (
                  <div key={id} className="ms-student-card selected readonly">
                    <div className="ms-student-avatar">{studentInitials(s)}</div>
                    <div className="ms-student-body">
                      <span className="ms-student-name">{studentName(s)}</span>
                      {s.batch && (
                        <span className="ms-student-batch-badge">{formatBatch(s.batch, batchLabels)}</span>
                      )}
                      {s.profile?.registrationNumber && (
                        <span className="ms-student-reg">{s.profile.registrationNumber}</span>
                      )}
                    </div>
                    <div className="ms-student-check">
                      <FaCheckCircle className="ms-check-icon selected" />
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────
  // CASE 2: HOD + HOD assigns → per-teacher accordion
  // ─────────────────────────────────────────────────────
  if (isHOD && assignmentMode === 'hod_assigns') {
    return (
      <div className="ms-container">
        <div className="ms-header">
          <div>
            <h2><FaUsers style={{ marginRight: 10 }} />My Students</h2>
            <p className="ms-subtitle">{assignedCourse?.name} ({assignedCourse?.code})</p>
          </div>
          <ModeToggle />
        </div>

        {saveSuccess && <div className="ms-save-alert"><FaCheckCircle /> {saveSuccess}</div>}

        <div className="ms-hod-warning">
          <strong>HOD Assigns Mode Active</strong> — Teachers cannot change their own student selection. You are assigning students for each teacher below.
        </div>

        <div className="ms-accordion">
          {hodTeachers.map((teacher) => {
            const isExpanded = expandedTeacherId === teacher.teacherId;
            const assigned = hodAssignments[teacher.teacherId] || [];
            const isMe = teacher.teacherId === (String(user?.id || user?._id));

            return (
              <div key={teacher.teacherId} className={`ms-accordion-card ${isExpanded ? 'expanded' : ''}`}>
                <div
                  className="ms-accordion-header"
                  onClick={() => setExpandedTeacherId(isExpanded ? null : teacher.teacherId)}
                >
                  <div className="ms-teacher-info">
                    <div className="ms-teacher-avatar">
                      {teacher.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <span className="ms-teacher-name">
                        {teacher.name}
                        {teacher.isHOD && <span className="ms-hod-chip">HOD</span>}
                        {isMe && <span className="ms-me-chip">You</span>}
                      </span>
                      <span className="ms-teacher-assigned">
                        {assigned.length} / {validStudents.length} assigned
                      </span>
                    </div>
                  </div>
                  <div className="ms-accordion-controls">
                    <button
                      className="ms-btn ms-btn-sm ms-btn-primary"
                      onClick={(e) => { e.stopPropagation(); saveOneTeacher(teacher.teacherId); }}
                      disabled={savingTeacherId === teacher.teacherId}
                    >
                      <FaSave />
                      {savingTeacherId === teacher.teacherId ? 'Saving...' : 'Save'}
                    </button>
                    <span className="ms-accordion-chevron">
                      {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </span>
                  </div>
                </div>

                {isExpanded && (
                  <div className="ms-accordion-body">
                    <StudentGrid
                      allStudents={validStudents}
                      selectedIds={assigned}
                      onToggle={(id) => hodToggle(teacher.teacherId, id)}
                      onSelectAll={() => hodSelectAll(teacher.teacherId)}
                      onDeselectAll={() => hodDeselectAll(teacher.teacherId)}
                      readOnly={false}
                      batchLabels={batchLabels}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {hodTeachers.length > 0 && (
          <div className="ms-save-all-bar">
            <button
              className="ms-btn ms-btn-primary ms-btn-lg"
              onClick={saveAllTeachers}
              disabled={savingAll}
            >
              <FaSave /> {savingAll ? 'Saving All...' : 'Save All Assignments'}
            </button>
          </div>
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────
  // CASE 3: HOD + Teachers Select → normal view + toggle
  // CASE 4: Regular teacher + Teachers Select → normal view
  // ─────────────────────────────────────────────────────
  const filteredStudents = validStudents
    .filter((s) => {
      if (!search) return true;
      const n = studentName(s).toLowerCase();
      const e = (s.email || '').toLowerCase();
      return n.includes(search.toLowerCase()) || e.includes(search.toLowerCase());
    })
    .sort((a, b) => studentName(a).localeCompare(studentName(b)));

  return (
    <div className="ms-container">
      <div className="ms-header">
        <div>
          <h2><FaUsers style={{ marginRight: 10 }} />My Students</h2>
          <p className="ms-subtitle">
            Select the students you are observing. Only their logbooks, checklists, and assessments will be shown in other views.
          </p>
        </div>
        {isHOD && <ModeToggle />}
      </div>

      {saveSuccess && <div className="ms-save-alert"><FaCheckCircle /> {saveSuccess}</div>}

      <div className="ms-toolbar">
        <div className="ms-search-box">
          <FaSearch className="ms-search-icon" />
          <input
            type="text"
            placeholder="Search students by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ms-search-input"
          />
          {search && (
            <button className="ms-search-clear" onClick={() => setSearch('')}>
              <FaTimes />
            </button>
          )}
        </div>
        <div className="ms-toolbar-right">
          <span className="ms-count-label">
            {myStudentIds.length} of {validStudents.length} selected
          </span>
          <button
            className="ms-btn ms-btn-sm ms-btn-outline"
            onClick={() => setMyStudentIds(validStudents.map((s) => String(s._id || s.id)))}
          >
            Select All
          </button>
          <button
            className="ms-btn ms-btn-sm ms-btn-outline"
            onClick={() => setMyStudentIds([])}
          >
            Deselect All
          </button>
          <button
            className="ms-btn ms-btn-sm ms-btn-pdf"
            onClick={() => handleDownloadPDF(validStudents)}
            disabled={pdfGenerating || validStudents.length === 0}
            title="Download student directory PDF"
          >
            <FaFilePdf /> {pdfGenerating ? 'Generating…' : 'Download PDF'}
          </button>
          <button
            className="ms-btn ms-btn-primary"
            onClick={saveOwn}
            disabled={saving}
          >
            <FaSave /> {saving ? 'Saving...' : 'Save Selection'}
          </button>
        </div>
      </div>

      {validStudents.length === 0 ? (
        <div className="ms-empty">
          <p>No students enrolled in this course.</p>
        </div>
      ) : filteredStudents.length === 0 ? (
        <p className="ms-no-results">No students match your search.</p>
      ) : (
        <div className="ms-student-grid">
          {filteredStudents.map((s) => {
            const id = String(s._id || s.id);
            const selected = myStudentIds.includes(id);
            return (
              <div
                key={id}
                className={`ms-student-card ${selected ? 'selected' : ''}`}
                onClick={() => toggleOwn(id)}
              >
                <div className="ms-student-avatar">{studentInitials(s)}</div>
                <div className="ms-student-body">
                  <span className="ms-student-name">{studentName(s)}</span>
                  {s.batch && (
                    <span className="ms-student-batch-badge">{formatBatch(s.batch, batchLabels)}</span>
                  )}
                  <span className="ms-student-reg">{s.email || ''}</span>
                </div>
                <div className="ms-student-check">
                  {selected ? (
                    <FaCheckCircle className="ms-check-icon selected" />
                  ) : (
                    <div className="ms-check-circle" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyStudents;
